import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { FormLabel } from "../../components/forms";
import { useClientContext } from "../../hooks/ClientContext";
import BusinessSheetSearch from "../bizsheetsearch";
import ClientReportSearch from "../../components/forms/selects/ClientReportSearchSelect";
import useCreateBusinessSheet from "./hooks/useCreateBusinessSheet";
import ThemedModal from "../../components/utilities/ThemedModal";

// Forbid creating biz sheets for Compliant (1/5/12), Cancelled (10), Execution Only (13), and Sent to Client (17) statuses
// Also forbid creating a new sheet for a report with a WP2 status: Finished (7), Follow Up (14), Migrate Recommendations (16)
const disabledReportStatuses = [1, 5, 7, 10, 12, 13, 14, 16, 17];

const CreateBusinessSheetModal = ({ handleClose, show, setSelectedSheetId }) => {
    const { account: { masterAccountId } } = useClientContext();
    const { defaultValues, handleCreate, isLoading: isCreating } = useCreateBusinessSheet(masterAccountId, null);
    const { control, formState: { errors }, handleSubmit, reset, setValue, watch, clearErrors } = useForm({ defaultValues });

    const hasSelectedReport = watch('reportId');
    const isBorrowing = watch('borrow');
    const borrowedSheetName = watch('borrowedSheetName');

    useEffect(() => {
        if (!isBorrowing) {
            clearErrors('borrowedSheetId');
            setValue('borrowedSheetId', null);
            setValue('borrowedSheetName', "");
        }
    }, [clearErrors, isBorrowing, setValue])

    const onClose = () => {
        reset(defaultValues);
        handleClose();
    };

    const onReportChanged = (report) => {
        clearErrors('reportId');
        setValue('masterAccountId', report.masterAccountId);
        setValue('reportMasterAccountId', report.masterAccountId);
        setValue('reportId', report.id);
        setValue('reportStatusId', report.statusId)
        setValue('projectId', report.projectId);
        setValue('reportName', report.reportName);
    };

    const onSubmit = (data) => {
        toast.promise(handleCreate(data)
            .then(r => {
                setSelectedSheetId(r.businessSheetId);

                return r.reportName;
            }), {
            loading: "Creating Business Sheet...",
            error: (err) => ['Error creating Business Sheet', err?.data?.message ?? err?.message].filter(Boolean).join(":\n"),
            success: (reportName) => {
                onClose();
                return `'${reportName}' Business Sheet Created! Please add an Instruction.`
            }
        })
    };

    const filterCompliantOptions = (options) => options.filter((o) => !o.isDisabled && !disabledReportStatuses.includes(o.data.status));

    return <Modal size="lg" centered show={show} onHide={handleClose}>
        <ThemedModal.Header closeButton>
            <Modal.Title>Create a New Business Sheet</Modal.Title>
        </ThemedModal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body>
                <div className="row mb-3">
                    <div className="col">
                        <Controller
                            name="reportId"
                            control={control}
                            rules={({ required: "A Report must be assigned to a Business Sheet." })}
                            render={({ field: { ref, value, onChange, ...rest } }) => (
                                <ClientReportSearch
                                    buttonSize="sm"
                                    label="Report"
                                    labelPosition="top"
                                    formError={errors?.reportId?.message}
                                    value={value}
                                    onReportCreated={(report) => onReportChanged(report)}
                                    onReportSelected={(_, report) => onReportChanged(report)}
                                    reportSelectProps={{ transformOptions: filterCompliantOptions }}
                                    {...rest}
                                />
                            )}
                        />
                    </div>
                </div>
                {hasSelectedReport && <div className="row">
                    <div className="col">
                        <FormLabel>Borrow?</FormLabel>
                        <Controller
                            name="borrowedSheetId"
                            control={control}
                            rules={{ required: { value: isBorrowing, message: "Please select a Business Sheet to borrow." } }}
                            render={({ field: { value, onChange, onBlur, ...rest } }) =>
                                <BusinessSheetSearch
                                    value={borrowedSheetName}
                                    placeholder="Search for a Business Sheet to borrow..."
                                    onSelect={(sheet) => {
                                        clearErrors('borrowedSheetId');
                                        setValue('borrowedSheetName', sheet.reportName);
                                        setValue('borrowedSheetId', sheet.id);
                                    }}
                                    formError={errors?.borrowedSheetId}
                                    showCheckbox
                                    isChecked={isBorrowing}
                                    setIsChecked={(checked) => setValue('borrow', checked)}
                                    disabled={!isBorrowing}
                                    {...rest}
                                />
                            }
                        />
                    </div>
                </div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" type="submit" disabled={isCreating}>Create Business Sheet</Button>
                <Button variant="light" onClick={onClose} disabled={isCreating}>Close</Button>
            </Modal.Footer>
        </form>
    </Modal>
};

export default CreateBusinessSheetModal;